import {
  IonContent,
  IonPage,
  IonButton,
  IonCard,
  IonCardContent,
} from '@ionic/react';
import { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { useWallet } from '../contexts/WalletContext';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import { playCelebrationSound } from '../utils/soundUtils';
import './WordSearch.css';

const GRID_SIZE = 12;
const WORDS = [
  'COINS', 'REWARD', 'GAMES', 'PLAY', 'WIN',
  'PRIZE', 'BONUS', 'LEVEL', 'SCORE', 'LUCKY',
  'GOLD', 'TREASURE', 'FUN', 'CHALLENGE', 'SUCCESS'
];

type Cell = {
  letter: string;
  selected: boolean;
  found: boolean;
  row: number;
  col: number;
};

type Word = {
  word: string;
  found: boolean;
  positions: { row: number; col: number }[];
};

const WordSearch: React.FC = () => {
  const { earnCoins } = useWallet();
  const [grid, setGrid] = useState<Cell[][]>([]);
  const [words, setWords] = useState<Word[]>([]);
  const [selectedCells, setSelectedCells] = useState<Cell[]>([]);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [gameOver, setGameOver] = useState(false);
  const [foundCount, setFoundCount] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    initializeGame();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!gameOver && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [gameOver, timeLeft]);

  const initializeGame = () => {
    // Select random words
    const selectedWords = [...WORDS].sort(() => Math.random() - 0.5).slice(0, 8);
    const newGrid: Cell[][] = Array(GRID_SIZE).fill(null).map(() => 
      Array(GRID_SIZE).fill(null).map(() => ({
        letter: '',
        selected: false,
        found: false,
        row: 0,
        col: 0,
      }))
    );

    // Fill with random letters
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let j = 0; j < GRID_SIZE; j++) {
        newGrid[i][j] = {
          letter: String.fromCharCode(65 + Math.floor(Math.random() * 26)),
          selected: false,
          found: false,
          row: i,
          col: j,
        };
      }
    }

    // Place words
    const wordObjects: Word[] = [];
    selectedWords.forEach(word => {
      const placed = placeWord(word, newGrid);
      if (placed) {
        wordObjects.push(placed);
      }
    });

    setGrid(newGrid);
    setWords(wordObjects);
    setSelectedCells([]);
    setTimeLeft(300);
    setGameOver(false);
    setFoundCount(0);
  };

  const placeWord = (word: string, grid: Cell[][]): Word | null => {
    const directions = [
      { dr: 0, dc: 1 },   // horizontal
      { dr: 1, dc: 0 },   // vertical
      { dr: 1, dc: 1 },   // diagonal down-right
      { dr: 1, dc: -1 },  // diagonal down-left
    ];

    for (let attempt = 0; attempt < 50; attempt++) {
      const dir = directions[Math.floor(Math.random() * directions.length)];
      const startRow = Math.floor(Math.random() * (GRID_SIZE - word.length * Math.abs(dir.dr)));
      const startCol = Math.floor(Math.random() * (GRID_SIZE - word.length * Math.abs(dir.dc)));

      if (startRow < 0 || startCol < 0) continue;

      const positions: { row: number; col: number }[] = [];
      let canPlace = true;

      for (let i = 0; i < word.length; i++) {
        const row = startRow + dir.dr * i;
        const col = startCol + dir.dc * i;
        
        if (row < 0 || row >= GRID_SIZE || col < 0 || col >= GRID_SIZE) {
          canPlace = false;
          break;
        }

        if (grid[row][col].letter !== '' && grid[row][col].letter !== word[i]) {
          canPlace = false;
          break;
        }

        positions.push({ row, col });
      }

      if (canPlace) {
        positions.forEach((pos, i) => {
          grid[pos.row][pos.col].letter = word[i];
        });
        return { word, found: false, positions };
      }
    }

    return null;
  };

  const handleCellClick = (cell: Cell) => {
    if (gameOver || cell.found) return;

    const newSelected = [...selectedCells];
    const cellIndex = newSelected.findIndex(c => c.row === cell.row && c.col === cell.col);

    if (cellIndex >= 0) {
      // Deselect
      newSelected.splice(cellIndex, 1);
    } else {
      // Select
      newSelected.push(cell);
      
      // Check if we have a valid word
      if (newSelected.length >= 3) {
        checkWord(newSelected);
      }
    }

    setSelectedCells(newSelected);
    
    // Update grid selection state
    setGrid(prev => prev.map(row => 
      row.map(c => ({
        ...c,
        selected: newSelected.some(sc => sc.row === c.row && sc.col === c.col),
      }))
    ));
  };

  const checkWord = (cells: Cell[]) => {
    const word = cells.map(c => c.letter).join('');
    const reversedWord = word.split('').reverse().join('');

    const foundWord = words.find(w => 
      !w.found && (w.word === word || w.word === reversedWord)
    );

    if (foundWord) {
      // Mark word as found
      const updatedWords = words.map(w => 
        w.word === foundWord.word ? { ...w, found: true } : w
      );
      setWords(updatedWords);

      // Mark cells as found
      setGrid(prev => prev.map(row => 
        row.map(c => {
          const isFound = foundWord.positions.some(
            p => p.row === c.row && p.col === c.col
          );
          return isFound ? { ...c, found: true, selected: false } : c;
        })
      ));

      setSelectedCells([]);
      setFoundCount(prev => prev + 1);

      // Check if all words found
      if (updatedWords.every(w => w.found)) {
        handleWin();
      }
    }
  };

  const handleWin = async () => {
    setGameOver(true);
    playCelebrationSound();
    
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });

    const timeBonus = Math.floor(timeLeft / 10);
    const coins = 150 + timeBonus;
    
    try {
      await earnCoins(coins, `Word Search - Found all words!`, 'word-search');
    } catch (error) {
      console.error('Error awarding coins:', error);
    }
  };

  const handleTimeUp = async () => {
    setGameOver(true);
    // Only award coins if at least one word was found
    const coins = foundCount > 0 ? 50 + foundCount * 10 : 0;
    
    if (coins > 0) {
      try {
        await earnCoins(coins, `Word Search - Found ${foundCount} words`, 'word-search');
      } catch (error) {
        console.error('Error awarding coins:', error);
      }
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <IonPage>
      <AppHeader title="Word Search" showBackButton={true} defaultHref="/games" />
      <IonContent fullscreen className="ion-padding">
        <div className="word-search-container">
          <IonCard className="game-info-card">
            <IonCardContent>
              <div className="info-row">
                <div className="info-item">
                  <span className="info-label">Time</span>
                  <span className="info-value">{formatTime(timeLeft)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Found</span>
                  <span className="info-value">{foundCount}/{words.length}</span>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          <div className="words-list">
            {words.map((word, index) => (
              <span
                key={index}
                className={`word-item ${word.found ? 'found' : ''}`}
              >
                {word.word}
              </span>
            ))}
          </div>

          <div className="game-board">
            <div className="word-grid">
              {grid.map((row, i) =>
                row.map((cell, j) => (
                  <div
                    key={`${i}-${j}`}
                    className={`grid-cell ${cell.selected ? 'selected' : ''} ${cell.found ? 'found' : ''}`}
                    onClick={() => handleCellClick(cell)}
                  >
                    {cell.letter}
                  </div>
                ))
              )}
            </div>
          </div>

          {gameOver && (
            <IonCard className="game-over-card">
              <IonCardContent>
                <h2>{foundCount === words.length ? '🎉 You Win!' : 'Time\'s Up!'}</h2>
                <p>Found {foundCount} out of {words.length} words</p>
                <IonButton expand="block" color="primary" onClick={initializeGame}>
                  Play Again
                </IonButton>
              </IonCardContent>
            </IonCard>
          )}

          <IonCard className="info-card">
            <IonCardContent>
              <p><strong>How to Play:</strong></p>
              <p>Click on letters to select them and form words. Find all words before time runs out!</p>
              <p className="coin-info">💰 Coins: 50 base + 10 per word + time bonus!</p>
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
      <BottomNav />
    </IonPage>
  );
};

export default WordSearch;

